import { AxiosResponse, HttpStatusCode } from 'axios';
import { VendorBusinessInfoForm } from './VendorBusinessDetails';
import toast from 'react-hot-toast';
import { Vendor } from '../../../types/dbTypes';

export type PersonalFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  companyPosition: string;
  address?: string;
  address_st_number?: string;
  address_st_name?: string;
  address_unit?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  termsAccepted?: boolean;
};

export const patchVendor = async (
  formData: VendorBusinessInfoForm,
  vendor_id: string,
  callProtectedApi: (
    url: string,
    requestType: string,
    body?: any,
    errorMsg?: string | undefined,
    newHeaders?: any,
    domain?: string | undefined,
    retries?: number
  ) => Promise<AxiosResponse<any, any> | null>,
  setLoading: (loading: boolean) => void,
  setStep: (newStep: string) => void,
  nextStep: string
) => {
  try {
    setLoading(true);
    const response = await callProtectedApi(`/vendors/${vendor_id}`, 'patch', {
      address_st_number: formData?.address_st_number,
      address_st_name: formData?.address_st_name,
      address_unit: formData?.address_unit || undefined,
      address_city: formData?.address_city,
      address_state: formData?.address_state,
      address_zip: formData?.address_zip,
      business_name: formData?.businessName,
      business_email: formData?.businessEmail,
      website_url: formData?.websiteUrl,
      business_phone_number: formData?.businessPhone,
      description_of_services: formData?.descriptionOfServices,
      hours_of_operation: JSON.stringify(formData?.hoursOfOperation),
      vendor_signup_step: nextStep,
      cb_consent_given: formData?.termsAccepted,
      employer_id_number: formData?.employerIdNumber,
      years_in_business: formData?.yearsInBusiness,
      primary_vendor_type: formData?.primary_vendor_type,
    });
    const vendor = response?.data?.data?.vendor;
    if (
      vendor &&
      Object.entries(vendor)?.length > 0 &&
      response?.status === HttpStatusCode.Ok
    ) {
      if (nextStep === 'vendor_personal_info') {
        const success = await createVendorBusinessLocation(
          formData,
          vendor_id,
          callProtectedApi,
          setLoading,
          vendor
        );
        if (success) {
          setStep(nextStep);
        } else {
          toast.error('Error occurred while creating vendor business location');
        }
      }
    }
  } catch (e: any) {
    console.error('Error occurred while updating vendor record: ', e);
    return null;
  } finally {
    setLoading(false);
  }
};

export const createVendorBusinessLocation = async (
  formData: VendorBusinessInfoForm,
  vendor_id: string,
  callProtectedApi: (
    url: string,
    requestType: string,
    body?: any,
    errorMsg?: string | undefined,
    newHeaders?: any,
    domain?: string | undefined,
    retries?: number
  ) => Promise<AxiosResponse<any, any> | null>,
  setLoading: (loading: boolean) => void,
  vendor: Vendor
): Promise<boolean> => {
  try {
    setLoading(true);
    const response = await callProtectedApi(
      `/vendorBusinessLocations`,
      'post',
      {
        vendor_id: vendor_id,
        address_st_number: formData?.address_st_number,
        address_st_name: formData?.address_st_name,
        address_unit: formData?.address_unit || undefined,
        address_city: formData?.address_city,
        address_state: formData?.address_state,
        address_zip: formData?.address_zip,
        email: formData?.businessEmail,
        website_url: formData?.websiteUrl,
        business_phone_number: formData?.businessPhone,
        description_of_services: formData?.descriptionOfServices,
        hours_of_operation: JSON.stringify(formData?.hoursOfOperation),
        primary_vendor_type: formData?.primary_vendor_type,
        secondary_vendor_types: formData?.secondary_vendor_types,
        location_name: formData?.businessName,
      }
    );
    const vendorBusinessLocation = response?.data?.data?.vendorBusinessLocation;
    if (
      vendorBusinessLocation &&
      Object.entries(vendorBusinessLocation)?.length > 0 &&
      (response?.status === HttpStatusCode.Ok ||
        response?.status === HttpStatusCode.Created)
    ) {
      return true;
    }
    return false;
  } catch (e: any) {
    console.error(
      'Error occurred while creating vendor business location: ',
      e
    );
    return false;
  } finally {
    setLoading(false);
  }
};

export const updateVendorSignupStep = async (
  vendor_id: string,
  callProtectedApi: (
    url: string,
    requestType: string,
    body?: any,
    errorMsg?: string | undefined,
    newHeaders?: any,
    domain?: string | undefined,
    retries?: number
  ) => Promise<AxiosResponse<any, any> | null>,
  setLoading: (loading: boolean) => void,
  setStep: (newStep: string) => void,
  nextStep: string
) => {
  try {
    setLoading(true);
    const response = await callProtectedApi(`/vendors/${vendor_id}`, 'patch', {
      vendor_signup_step: nextStep,
    });
    const vendor = response?.data?.data?.vendor;
    if (
      vendor &&
      Object.entries(vendor)?.length > 0 &&
      response?.status === HttpStatusCode.Ok
    ) {
      setStep(nextStep);
    }
  } catch (e: any) {
    console.error(
      'Error occurred while updating vendor record signup step: ',
      e
    );
    return null;
  } finally {
    setLoading(false);
  }
};

export const createVendorContactInfo = async (
  formData: PersonalFormData,
  vendor_id: string,
  callProtectedApi: (
    url: string,
    requestType: string,
    body?: any,
    errorMsg?: string | undefined,
    newHeaders?: any,
    domain?: string | undefined,
    retries?: number
  ) => Promise<AxiosResponse<any, any> | null>,
  setLoading: (loading: boolean) => void,
  setStep: (newStep: string) => void,
  nextStep: string
) => {
  try {
    setLoading(true);
    const response = await callProtectedApi(`/vendorContactInfos`, 'post', {
      first_name: formData?.firstName,
      last_name: formData?.lastName,
      email: formData?.email,
      phone_number: formData?.phoneNumber,
      position: formData?.companyPosition,
      vendor_id: vendor_id,
      is_primary_owner: formData?.termsAccepted ?? false,
      address_city: formData?.address_city,
      address_state: formData?.address_state,
      address_zip: formData?.address_zip,
      address_st_name: formData?.address_st_name,
      address_st_number: formData?.address_st_number,
      address_unit: formData?.address_unit || null,
    });
    const vendorContact = response?.data?.data?.vendorContactInfo;
    if (
      vendorContact &&
      Object.entries(vendorContact)?.length > 0 &&
      (response?.status === HttpStatusCode.Created ||
        response?.status === HttpStatusCode.Ok)
    ) {
      setStep(nextStep);
      return true;
    }
    return false;
  } catch (e: any) {
    console.error('Error occurred while creating vendor contact info: ', e);
    return false;
  } finally {
    setLoading(false);
  }
};
